import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  SpinnerGap,
  Calendar,
  Clock,
  User,
  Phone,
  Envelope,
  CheckCircle,
  XCircle,
  CaretLeft,
  CaretRight,
  Wrench,
  CalendarBlank,
  Warning,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SERVICE_LABELS = {
  general: "General Service",
  repair: "Repair",
  installation: "Installation",
  maintenance: "Maintenance",
  inspection: "Inspection",
  emergency: "Emergency",
  consultation: "Consultation",
};

export default function PublicBookingPage() {
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [unavailableReason, setUnavailableReason] = useState(null);
  const [success, setSuccess] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  
  // Form state
  const [selectedService, setSelectedService] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [notes, setNotes] = useState("");
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchBookingInfo();
  }, [tenantId]);

  const fetchBookingInfo = async () => {
    try {
      const response = await fetch(`${API}/public/booking/${tenantId}`);
      const data = await response.json();
      
      if (data.available) {
        setBookingInfo(data);
        if (data.eligible_services?.length === 1) {
          setSelectedService(data.eligible_services[0]);
        }
      } else {
        setUnavailableReason(data.reason);
      }
    } catch (error) {
      console.error("Error fetching booking info:", error);
      setUnavailableReason("error");
    } finally {
      setLoading(false);
    }
  };

  // Generate available dates
  const availableDates = useMemo(() => {
    if (!bookingInfo) return [];
    
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() + (bookingInfo.booking_buffer_days || 1));
    
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + (bookingInfo.max_advance_days || 30));
    
    const current = new Date(startDate);
    while (current <= endDate) {
      const dayName = current.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
      const dayHours = bookingInfo.business_hours?.[dayName];
      
      if (dayHours && !dayHours.closed) {
        dates.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  }, [bookingInfo]);

  // Generate available times for selected date
  const availableTimes = useMemo(() => {
    if (!bookingInfo || !selectedDate) return [];
    
    const date = new Date(selectedDate);
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const dayHours = bookingInfo.business_hours?.[dayName];
    
    if (!dayHours || dayHours.closed) return [];
    
    const times = [];
    const [startHour, startMin] = (dayHours.start || "09:00").split(":").map(Number);
    const [endHour, endMin] = (dayHours.end || "17:00").split(":").map(Number);
    
    const duration = bookingInfo.default_duration || 60;
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const time = `${currentHour.toString().padStart(2, "0")}:${currentMin.toString().padStart(2, "0")}`;
      times.push(time);
      
      currentMin += 30;
      if (currentMin >= 60) {
        currentHour += 1;
        currentMin = 0;
      }
    }
    
    return times;
  }, [bookingInfo, selectedDate]);

  // Calendar helpers
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Add padding for first week
    const startPadding = firstDay.getDay();
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Add all days of month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    
    return days;
  }, [currentMonth]);

  const isDateAvailable = (date) => {
    if (!date) return false;
    return availableDates.some(
      (d) => d.toDateString() === date.toDateString()
    );
  };

  const isDateSelected = (date) => {
    if (!date || !selectedDate) return false;
    return new Date(selectedDate).toDateString() === date.toDateString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (time) => {
    const [hour, min] = time.split(":");
    const h = parseInt(hour);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${min} ${ampm}`;
  };

  const handleSubmit = async () => {
    // Validate
    if (!selectedService) {
      toast.error("Please select a service");
      return;
    }
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (!selectedTime) {
      toast.error("Please select a time");
      return;
    }
    if (!customerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!customerEmail.trim() || !customerEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    if (bookingInfo.require_phone && !customerPhone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/public/booking/${tenantId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_type: selectedService,
          scheduled_date: selectedDate,
          scheduled_time: selectedTime,
          customer_name: customerName.trim(),
          customer_email: customerEmail.trim(),
          customer_phone: customerPhone.trim() || null,
          notes: notes.trim() || null,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess(true);
        setConfirmationData(data);
      } else {
        toast.error(data.detail || "Failed to submit booking");
      }
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <SpinnerGap size={40} className="animate-spin text-[#FF6600]" />
      </div>
    );
  }

  // Unavailable states
  if (unavailableReason) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-2">
          <CardContent className="p-8 text-center">
            <XCircle size={48} className="mx-auto text-zinc-400 mb-4" />
            <h1 className="font-heading text-xl font-bold uppercase mb-2">
              Booking Unavailable
            </h1>
            <p className="text-zinc-500">
              {unavailableReason === "not_found" && "This booking page does not exist."}
              {unavailableReason === "not_entitled" && "Online booking is not available for this provider."}
              {unavailableReason === "disabled" && "Online booking is currently disabled."}
              {unavailableReason === "feature_inactive" && "Online booking is not available at this time."}
              {unavailableReason === "error" && "Unable to load booking information. Please try again later."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success && confirmationData) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-2 border-emerald-200">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={48} className="text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold uppercase mb-2 text-emerald-800">
              Booking Confirmed!
            </h1>
            <p className="text-zinc-600 mb-6">
              {confirmationData.confirmation_message || "Thank you for your booking!"}
            </p>
            
            <div className="bg-zinc-50 p-4 rounded-sm text-left space-y-2 mb-6">
              <div className="flex justify-between">
                <span className="text-zinc-500">Company</span>
                <span className="font-medium">{confirmationData.company_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Service</span>
                <span className="font-medium">{SERVICE_LABELS[selectedService] || selectedService}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Date</span>
                <span className="font-medium">{formatDate(selectedDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Time</span>
                <span className="font-medium">{formatTime(selectedTime)}</span>
              </div>
            </div>
            
            <p className="text-sm text-zinc-500">
              A confirmation has been sent to your email. We will contact you shortly!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 py-8 px-4" data-testid="public-booking-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
            {bookingInfo.company_name}
          </h1>
          <p className="text-zinc-500 mt-1">Book an Appointment Online</p>
        </div>

        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4 md:p-6 space-y-6">
            {/* Service Selection */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Wrench size={18} className="text-[#FF6600]" />
                Select Service
              </Label>
              <Select value={selectedService} onValueChange={setSelectedService}>
                <SelectTrigger data-testid="service-select">
                  <SelectValue placeholder="Choose a service..." />
                </SelectTrigger>
                <SelectContent>
                  {bookingInfo.eligible_services.map((service) => (
                    <SelectItem key={service} value={service}>
                      {SERVICE_LABELS[service] || service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection - Calendar */}
            <div>
              <Label className="flex items-center gap-2 mb-3">
                <CalendarBlank size={18} className="text-[#FF6600]" />
                Select Date
              </Label>
              
              {/* Calendar Navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                >
                  <CaretLeft size={20} />
                </Button>
                <span className="font-medium">
                  {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                >
                  <CaretRight size={20} />
                </Button>
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div key={d} className="text-xs text-zinc-500 py-2">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((date, idx) => (
                  <button
                    key={idx}
                    disabled={!isDateAvailable(date)}
                    onClick={() => date && setSelectedDate(date.toISOString().split("T")[0])}
                    className={`
                      aspect-square flex items-center justify-center text-sm rounded-sm transition-all
                      ${!date ? "invisible" : ""}
                      ${isDateSelected(date) ? "bg-[#FF6600] text-white font-bold" : ""}
                      ${isDateAvailable(date) && !isDateSelected(date) ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 cursor-pointer" : ""}
                      ${!isDateAvailable(date) && date ? "text-zinc-300 cursor-not-allowed" : ""}
                    `}
                    data-testid={date ? `date-${date.toISOString().split("T")[0]}` : undefined}
                  >
                    {date?.getDate()}
                  </button>
                ))}
              </div>
              
              {selectedDate && (
                <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1">
                  <CheckCircle size={14} />
                  {formatDate(selectedDate)}
                </p>
              )}
            </div>

            {/* Time Selection */}
            {selectedDate && availableTimes.length > 0 && (
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Clock size={18} className="text-[#FF6600]" />
                  Select Time
                </Label>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {availableTimes.map((time) => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`
                        py-2 px-3 text-sm rounded-sm border-2 transition-all
                        ${selectedTime === time
                          ? "border-[#FF6600] bg-[#FF6600]/10 text-[#FF6600] font-medium"
                          : "border-zinc-200 hover:border-zinc-300"
                        }
                      `}
                      data-testid={`time-${time}`}
                    >
                      {formatTime(time)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Customer Information */}
            <div className="border-t border-zinc-200 pt-6">
              <h3 className="font-heading text-sm uppercase mb-4 text-zinc-600">Your Information</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <User size={16} />
                    Full Name *
                  </Label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Smith"
                    className="mt-1"
                    data-testid="customer-name-input"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Envelope size={16} />
                    Email *
                  </Label>
                  <Input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="mt-1"
                    data-testid="customer-email-input"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone size={16} />
                    Phone {bookingInfo.require_phone ? "*" : "(optional)"}
                  </Label>
                  <Input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                    data-testid="customer-phone-input"
                  />
                </div>
                
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional details about your service needs..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedService || !selectedDate || !selectedTime || !customerName || !customerEmail}
              className="w-full bg-[#FF6600] hover:bg-[#CC5200] text-white py-6 text-lg"
              data-testid="submit-booking-btn"
            >
              {submitting ? (
                <SpinnerGap size={24} className="animate-spin" />
              ) : (
                <>
                  <Calendar size={20} className="mr-2" />
                  Book Appointment
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-400 mt-6">
          Powered by Free Service Pro
        </p>
      </div>
    </div>
  );
}
